import { LuRotateCcw } from 'react-icons/lu';
import { useShallow } from 'zustand/react/shallow';

import { FilterDropdown, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { Button } from '~components/ui/button';
import { MINE_SITES } from '~config';
import { selectActiveMineCode, useFilterStore } from '~store';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const OPTIMISATION_FILTER_OPTIONS = [
  { value: 'true', label: 'Has Optimisations' },
  { value: 'false', label: 'No Optimisations' },
] as const;

const DRILL_DOMAINS_FILTER_OPTIONS = [
  { value: 'true', label: 'Has Drill Domains' },
  { value: 'false', label: 'No Drill Domains' },
] as const;

const NEEDS_ATTENTION_FILTER_OPTIONS = [
  { value: 'true', label: 'Needs Attention' },
  { value: 'false', label: 'No Issues' },
] as const;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const parseBooleanFilter = (v: string | null): boolean | null => {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Filters for the blast list page.
 * Reads from and writes to the filter store directly.
 * Provides Mine Site, Optimisations, and Needs Attention filter dropdowns.
 */
const BlastListFilters = () => {
  const {
    setSelectedSite,
    hasOptimisation,
    setHasOptimisation,
    hasDrillDomains,
    setHasDrillDomains,
    needsAttention,
    setNeedsAttention,
    resetDropdownFilters,
  } = useFilterStore(
    useShallow((state) => ({
      setSelectedSite: state.setSelectedSite,
      hasOptimisation: state.hasOptimisation,
      setHasOptimisation: state.setHasOptimisation,
      hasDrillDomains: state.hasDrillDomains,
      setHasDrillDomains: state.setHasDrillDomains,
      needsAttention: state.needsAttention,
      setNeedsAttention: state.setNeedsAttention,
      resetDropdownFilters: state.resetDropdownFilters,
    })),
  );

  const activeMineCode = useFilterStore(selectActiveMineCode);
  const mineDisplayValue = activeMineCode ? MINE_SITES.find((site) => site.code === activeMineCode)?.name : 'All';
  const selectedOptimisationOption = OPTIMISATION_FILTER_OPTIONS.find((o) => o.value === String(hasOptimisation));
  const selectedDrillDomainsOption = DRILL_DOMAINS_FILTER_OPTIONS.find((o) => o.value === String(hasDrillDomains));
  const selectedNeedsAttentionOption = NEEDS_ATTENTION_FILTER_OPTIONS.find((o) => o.value === String(needsAttention));

  const hasActiveFilters =
    hasOptimisation !== null || hasDrillDomains !== null || needsAttention !== null || activeMineCode !== undefined;

  return (
    <>
      {/* Mine site */}
      <FilterDropdown
        label="Mine Site"
        value={activeMineCode ?? null}
        displayValue={mineDisplayValue}
        options={MINE_SITES.map(({ name, code }) => ({ value: code, label: name }))}
        onSelect={setSelectedSite}
      />

      {/* Optimisations */}
      <FilterDropdown
        label={selectedOptimisationOption?.label ?? 'Optimisations'}
        value={hasOptimisation === null ? null : String(hasOptimisation)}
        displayValue={selectedOptimisationOption ? null : 'All'}
        options={OPTIMISATION_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }))}
        onSelect={(v) => setHasOptimisation(parseBooleanFilter(v))}
      />

      {/* Has Drill domains */}
      <FilterDropdown
        label={selectedDrillDomainsOption?.label ?? 'Drill Domains'}
        value={hasDrillDomains === null ? null : String(hasDrillDomains)}
        displayValue={selectedDrillDomainsOption ? null : 'All'}
        options={DRILL_DOMAINS_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }))}
        onSelect={(v) => setHasDrillDomains(parseBooleanFilter(v))}
      />

      {/* Needs attention */}
      <FilterDropdown
        label={selectedNeedsAttentionOption?.label ?? 'Needs Attention'}
        value={needsAttention === null ? null : String(needsAttention)}
        displayValue={selectedNeedsAttentionOption ? null : 'All'}
        options={NEEDS_ATTENTION_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }))}
        onSelect={(v) => setNeedsAttention(parseBooleanFilter(v))}
      />

      {/* Reset Filters */}
      {hasActiveFilters && (
        <TooltipRoot>
          <TooltipTrigger asChild>
            <Button icon size="sm" variant="ghost" aria-label="Reset Filters" onClick={resetDropdownFilters}>
              <LuRotateCcw size={20} aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Reset Filters</TooltipContent>
        </TooltipRoot>
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlastListFilters };
